// Funciones puras para la administración de folios/dorsales: no tocan
// Firebase ni Redux, solo derivan sets/listas/celdas a partir del snapshot
// de /inscritos/folios/ y de la configuración de modalidades. Mismo criterio
// que bulkEditLogic.js/attributeLogic.js/gridLogic.js en este proyecto.

// Valor centinela para "folio reservado/bloqueado por un admin, sin dueño
// real todavía". Es un string NO vacío a propósito: las funciones de
// asignación automática en functions/modules/folios.js ya comparan
// `foliosLista[...].atleta` y `!atletaActual` por verdadero/falso, así que
// cualquier string no vacío ya se trata como "ocupado" sin tocar ese código.
// Un push key real de Firebase siempre tiene 20 caracteres, así que este
// centinela corto nunca puede coincidir por accidente con un inscritoKey.
export const FOLIO_RESERVADO_MARCADOR = "RESERVADO";

// Por encima de este tamaño de rango, la matriz se trunca (ver buildFolioCells).
export const MATRIZ_LIMITE_SIN_TRUNCAR = 500;
// Cuántos folios libres, a partir de "desde", se muestran en un rango truncado.
export const MATRIZ_PROXIMOS_LIBRES = 100;

function parseFolioNumero(key) {
  const numero = Number(key.replace("num_", ""));
  return Number.isFinite(numero) ? numero : null;
}

function rango(desde, hasta) {
  const out = [];
  for (let n = desde; n <= hasta; n++) out.push(n);
  return out;
}

// Map<numero, atleta> con todas las entradas del snapshot de /inscritos/folios/
// que tienen algo escrito en "atleta" (inscritoKey real o el centinela de
// reserva). Base compartida por los sets de abajo y por findDuplicateFolios.
export function extractFolioOwners(foliosSnapshot) {
  const owners = new Map();
  Object.keys(foliosSnapshot || {}).forEach((key) => {
    const numero = parseFolioNumero(key);
    if (numero === null) return;
    const atleta = (foliosSnapshot[key] || {}).atleta;
    if (atleta) owners.set(numero, atleta);
  });
  return owners;
}

export function extractAsignadosSet(foliosSnapshot) {
  const owners = extractFolioOwners(foliosSnapshot);
  const set = new Set();
  owners.forEach((atleta, numero) => {
    if (atleta !== FOLIO_RESERVADO_MARCADOR) set.add(numero);
  });
  return set;
}

export function extractReservadosSet(foliosSnapshot) {
  const owners = extractFolioOwners(foliosSnapshot);
  const set = new Set();
  owners.forEach((atleta, numero) => {
    if (atleta === FOLIO_RESERVADO_MARCADOR) set.add(numero);
  });
  return set;
}

// Celdas a renderizar para un rango [desde, hasta] dado un set de folios
// ocupados (asignados ∪ reservados). Si el rango cabe completo, se devuelve
// tal cual; si no, se arma como la unión ordenada de "ocupados dentro del
// rango" + "próximos N libres desde el inicio" + "hasta", con "..." en cada
// salto — así ningún folio ocupado queda oculto sin importar su posición.
// "forzarCompleto" ignora el truncado (lo usa la UI cuando el admin hace
// clic en un "..." para desplegar el rango completo de esa modalidad).
export function buildFolioCells(desde, hasta, ocupados, forzarCompleto = false) {
  if (desde > hasta) return [];

  const rangeSize = hasta - desde + 1;
  if (forzarCompleto || rangeSize <= MATRIZ_LIMITE_SIN_TRUNCAR) {
    return rango(desde, hasta).map((n) => ({ type: "folio", numero: n }));
  }

  const ocupadosEnRango = [...ocupados].filter((n) => n >= desde && n <= hasta);

  const proximosLibres = [];
  for (let n = desde; n <= hasta && proximosLibres.length < MATRIZ_PROXIMOS_LIBRES; n++) {
    if (!ocupados.has(n)) proximosLibres.push(n);
  }

  const aMostrar = [...new Set([...ocupadosEnRango, ...proximosLibres, hasta])].sort(
    (a, b) => a - b
  );

  const cells = [];
  aMostrar.forEach((n, i) => {
    if (i > 0 && n - aMostrar[i - 1] > 1) cells.push({ type: "ellipsis" });
    cells.push({ type: "folio", numero: n });
  });
  return cells;
}

// Rangos desde/hasta configurados por modalidad, a partir de
// state.events.eventSubEventsRegistry. Ignora modalidades sin rango de
// folios configurado.
export function listModalidadRanges(eventSubEventsRegistry) {
  if (!eventSubEventsRegistry) return [];
  return Object.keys(eventSubEventsRegistry)
    .filter((k) => k.indexOf("modalidad_") === 0)
    .map((k) => {
      const modalidad = eventSubEventsRegistry[k] || {};
      const config = (modalidad.configModalidad && modalidad.configModalidad.folios) || {};
      const desde = Number(config.desde);
      const hasta = Number(config.hasta);
      return {
        codigo: k.replace("modalidad_", ""),
        label: modalidad.descripcionModalidad || k.replace("modalidad_", ""),
        desde: Number.isFinite(desde) ? desde : null,
        hasta: Number.isFinite(hasta) ? hasta : null,
      };
    })
    .filter((m) => m.desde !== null && m.hasta !== null);
}

export function countInRange(desde, hasta, asignados, reservados) {
  if (desde > hasta) return { total: 0, asignados: 0, reservados: 0, libres: 0 };
  const total = hasta - desde + 1;
  let asignadosCount = 0;
  let reservadosCount = 0;
  asignados.forEach((n) => {
    if (n >= desde && n <= hasta) asignadosCount++;
  });
  reservados.forEach((n) => {
    if (n >= desde && n <= hasta) reservadosCount++;
  });
  return {
    total,
    asignados: asignadosCount,
    reservados: reservadosCount,
    libres: total - asignadosCount - reservadosCount,
  };
}

// Fusiona rangos [desde, hasta] que se traslapan o son contiguos en la
// menor cantidad de intervalos disjuntos posible. /inscritos/folios/ es un
// único nodo plano por evento (no uno por modalidad): si dos modalidades
// comparten o traslapan su rango, están compitiendo por los MISMOS números
// físicos, no por números independientes.
function fusionarRangos(rangos) {
  const ordenados = (rangos || [])
    .filter((r) => r.desde <= r.hasta)
    .map((r) => ({ desde: r.desde, hasta: r.hasta }))
    .sort((a, b) => a.desde - b.desde);

  const fusionados = [];
  ordenados.forEach((r) => {
    const ultimo = fusionados[fusionados.length - 1];
    if (ultimo && r.desde <= ultimo.hasta + 1) {
      ultimo.hasta = Math.max(ultimo.hasta, r.hasta);
    } else {
      fusionados.push({ ...r });
    }
  });
  return fusionados;
}

// Totales reales del evento: la unión de los rangos de todas las
// modalidades, no la suma nominal de cada una. Si varias modalidades
// comparten el mismo rango (ej. las 3 usan 1-1000), el "foliado real" son
// esos 1000 números físicos, no 3000 — y un folio ocupado en ese rango
// compartido resta de los libres una sola vez, sin importar a cuántas
// modalidades pertenezca.
export function consolidateTotals(modalidades, asignados, reservados) {
  const union = fusionarRangos(modalidades);

  let total = 0;
  union.forEach((r) => {
    total += r.hasta - r.desde + 1;
  });

  function estaEnLaUnion(numero) {
    return union.some((r) => numero >= r.desde && numero <= r.hasta);
  }

  let asignadosCount = 0;
  asignados.forEach((n) => {
    if (estaEnLaUnion(n)) asignadosCount++;
  });
  let reservadosCount = 0;
  reservados.forEach((n) => {
    if (estaEnLaUnion(n)) reservadosCount++;
  });

  return {
    total,
    asignados: asignadosCount,
    reservados: reservadosCount,
    libres: total - asignadosCount - reservadosCount,
  };
}

export function lookupFolio(numero, asignados, reservados, modalidades) {
  const modalidadMatch = (modalidades || []).find(
    (m) => numero >= m.desde && numero <= m.hasta
  );
  const status = asignados.has(numero)
    ? "assigned"
    : reservados.has(numero)
    ? "reserved"
    : "unassigned";
  return { numero, status, inRange: !!modalidadMatch, modalidad: modalidadMatch || null };
}

// Detecta inscritos con más de un folio vinculado en /inscritos/folios/
// (bug de datos, no debería pasar). El folio cuyo número coincide con el
// "numero" oficial del inscrito (el mismo campo que escribe
// AsignarNumeroDefinitivoNew en el backend) se considera el legítimo; el
// resto se reporta como duplicado/bloqueado, listo para liberar. Si ninguno
// coincide, se reportan todos como ambiguos para que el admin decida.
export function findDuplicateFolios(foliosSnapshot, inscritosByKey) {
  const owners = extractFolioOwners(foliosSnapshot);
  const porAtleta = new Map();
  owners.forEach((atleta, numero) => {
    if (atleta === FOLIO_RESERVADO_MARCADOR) return;
    if (!porAtleta.has(atleta)) porAtleta.set(atleta, []);
    porAtleta.get(atleta).push(numero);
  });

  const duplicados = [];
  porAtleta.forEach((numeros, inscritoKey) => {
    if (numeros.length < 2) return;
    const inscrito = (inscritosByKey || {})[inscritoKey];
    const numeroOficial = inscrito ? Number(inscrito.numero) : null;
    const legitimo = numeros.find((n) => n === numeroOficial);

    numeros
      .filter((n) => n !== legitimo)
      .sort((a, b) => a - b)
      .forEach((numero) => {
        duplicados.push({
          numero,
          inscritoKey,
          nombreAtleta: inscrito
            ? `${inscrito.nombres || ""} ${inscrito.apellidos || ""}`.trim()
            : null,
          folioLegitimo: legitimo !== undefined ? legitimo : null,
          ambiguo: legitimo === undefined,
        });
      });
  });

  return duplicados.sort((a, b) => a.numero - b.numero);
}

// Detecta inscritos cuyo folio asignado ("numero" en su registro) queda
// fuera del rango desde/hasta configurado para SU PROPIA modalidad — típico
// de una asignación manual de un folio específico (UpdateRaceNumber.jsx no
// valida contra el rango de la modalidad, solo que el número no esté
// ocupado). La info sale de eventParticipants (Redux), no de
// /inscritos/folios/, porque ahí es donde vive la relación
// atleta -> modalidad -> folio asignado.
export function findFoliosFueraDeRango(eventParticipants, modalidades) {
  const porCodigo = new Map((modalidades || []).map((m) => [String(m.codigo), m]));
  const fueraDeRango = [];

  Object.keys(eventParticipants || {}).forEach((inscritoKey) => {
    const participante = eventParticipants[inscritoKey] || {};
    const numero = Number(participante.numero);
    if (!Number.isFinite(numero) || numero <= 0) return;
    if (participante.modalidad === undefined || participante.modalidad === null) return;

    const modalidad = porCodigo.get(String(participante.modalidad));
    if (!modalidad) return; // modalidad sin rango de folios configurado: no aplica

    if (numero < modalidad.desde || numero > modalidad.hasta) {
      fueraDeRango.push({
        numero,
        inscritoKey,
        modalidadCodigo: modalidad.codigo,
        nombreAtleta: `${participante.nombres || ""} ${participante.apellidos || ""}`.trim(),
      });
    }
  });

  return fueraDeRango.sort((a, b) => a.numero - b.numero);
}
